// components/welcomeConfig.ts
import { getAuthCookie } from "../../utils/authCookies";
import {
  Clipboard,
  ClipboardList,
  Truck,
  Package,
  FileText,
  Users,
  Star,
  DollarSign,
  TrendingUp,
  Camera,
  Target,
  Shield,
  MapPin,
  Activity,
  FolderOpen,
  Folder,
  Building2,
  MessageSquare,
  Handshake,
  CreditCard,
  Store,
  Map,
  Zap,
  Tag,
} from "lucide-react";

export const routePrefixMap: Record<string, string> = {
  SIGNAGE_FIRM: "signage",
  SIGNAGE_TEAM_MEMBER: "signageteammember",
  BROKERAGE_FIRM: "realtor",
  BROKERAGE_TEAM_MEMBER: "teammember",
  BROKERAGE_AGENT: "agent",
  AGENT: "agent",
};


export type WelcomeType =
  | "SIGNAGE_FIRM"
  | "BROKERAGE_FIRM"
  | "BROKERAGE_TEAM_MEMBER"
  | "SIGNAGE_TEAM_MEMBER"
  | "BROKERAGE_AGENT"
  | "AGENT";

const hideTipsFor = ["AGENT"];
const license = getAuthCookie("licenseType") as WelcomeType;
export const prefix = routePrefixMap[license] ?? "signage";



export const welcomeConfig: Record<WelcomeType, any> = {

  // -------------------------------------------------------------------
  // SIGNAGE + TEAM MEMBER
  // -------------------------------------------------------------------
  SIGNAGE_FIRM: {
    hideTips: false,
    heroTitle: "RealEsy — Signage Vendor Hub",
    heroSubtitle:
      "Your unified workspace for managing real-estate signage installations, removals, inventory, and billing — all in one place.",

    whatYouCanDo: [
      {
        icon: <ClipboardList size={24} color="#155dfc" />,
        iconBg: "#dbeafe",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Installation Jobs",
        desc:
          "Create, schedule, and manage installations with real-time updates, photo proof, and seamless communication with brokerages and agents.",
        generatePath: (prefix: string) => `/${prefix}/jobs/installations`,
      },
      {
        icon: <MapPin size={24} color="#f54a00" />,
        iconBg: "#ffedd4",
        iconBorder: "1px solid rgba(250,127,45,0.18)",
        title: "Removal Jobs",
        desc:
          "View upcoming removal requests, assign installers, track progress, and restock signs back to warehouse inventory.",
        generatePath: (prefix: string) => `/${prefix}/jobs/removalRequests`,
      },
      {
        icon: <Package size={24} color="#9810fa" />,
        iconBg: "#f3e8ff",
        iconBorder: "1px solid rgba(139,72,255,0.18)",
        title: "Inventory Management",
        desc:
          "Track warehouse stock, signs currently in the field, and items returned from removal jobs. Maintain accuracy and prevent losses.",
        generatePath: (prefix: string) => `/${prefix}/products`,
      },
      {
        icon: <CreditCard size={24} color="#00a63e" />,
        iconBg: "#dcfce7",
        iconBorder: "1px solid rgba(43,168,74,0.18)",
        title: "Sales Orders & Invoicing",
        desc:
          "Manage orders, collect payments, track outstanding balances, and review billing history.",
        generatePath: (prefix: string) => `/${prefix}/orders`,
      },
      {
        icon: <Users size={24} color="#4f39f6" />,
        iconBg: "#e0e7ff",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Team Management",
        desc: "Control access for installers, admin staff, and managers with role-based permissions.",
      },
    ],

    tips: [
      {
        bg: "#fefce8",
        icon: <Star size={22} color="#364153" />,
        bordercolor: "#fff085",
        title: "Complete Your Marketplace Profile",
        text: "A polished Marketplace profile helps you win more jobs from brokerages:",
        items: [
          "Add your logo & brand colors",
          "Coverage areas",
          "Turnaround times",
          "Services offered (rush installs, after-hours, rider printing, etc.)",
          "Contact details",
        ],
        note: "A complete profile increases your ranking and visibility.",
      },
      {
        bg: "#f1fff4",
        icon: <DollarSign size={22} color="#364153" />,
        bordercolor: "#b9f8cf",
        title: "Offer Smart Discounts to Attract More Orders",
        text: "Use optional discount settings to stand out in the marketplace:",
        items: [
          "New brokerage discount",
          "Volume-based discounts",
          "Seasonal promotions",
          "“First 10 orders” discount",
        ],
        note: "These appear directly in your Marketplace listing.",
      },
      {
        bg: "#eff6ff",
        icon: <TrendingUp size={22} color="#364153" />,
        bordercolor: "#bedbff",
        title: "Keep Your Pricing Updated & Competitive",
        text:
          "Clear, accurate pricing builds trust and reduces back-and-forth between agents and your team.",
      },
      {
        bg: "#faf5ff",
        icon: <Target size={22} color="#364153" />,
        bordercolor: "#e9d4ff",
        title: "Improve Your SLA & Job Ratings",
        text: "Higher performance increases your visibility:",
        items: [
          "Update job statuses on time",
          "Upload clear photo proof",
          "Respond quickly to agent messages",
          "Minimize delayed or rescheduled jobs",
        ],
        note: "RealEsy highlights top-performing vendors to brokerages.",
      },
      {
        bg: "#fff7ed",
        icon: <Package size={22} color="#364153" />,
        bordercolor: "#ffd7a8",
        title: "Track Signs in Field Regularly",
        text:
          "Stay on top of signs deployed in the field to avoid losses, damaged items, or missing inventory. Accurate tracking also improves billing and operational efficiency.",
      },
      {
        bg: "#eef2ff",
        icon: <Map size={22} color="#364153" />,
        bordercolor: "#c6d2ff",
        title: "Use Route-Aware Assignments",
        text:
          "Assign installers based on proximity to reduce travel time and improve SLA compliance.",
      },
      {
        bg: "#fdf2f8",
        icon: <Activity size={22} color="#364153" />,
        bordercolor: "#fccee8",
        title: "Stay Active",
        text:
          "The more active your team is—completing jobs, updating statuses, managing inventory—the more RealEsy's Marketplace algorithm prioritizes your vendor profile.",
      },
    ],
    quickActions: [
      {
        label: "Go to Installations",
        icon: <ClipboardList size={20} color="#155dfc" />,
        bg: "rgba(21,93,252,0.12)",
        generatePath: (prefix: string) => `/${prefix}/jobs/installations`,
      },
      {
        label: "Go to Removals",
        icon: <MapPin size={20} color="#fa7f2d" />,
        bg: "rgba(250,127,45,0.12)",
        generatePath: (prefix: string) => `/${prefix}/jobs/removalRequests`,
      },
      {
        label: "Go to Inventory",
        icon: <Package size={20} color="#8b48ff" />,
        bg: "rgba(139,72,255,0.12)",
        generatePath: (prefix: string) => `/${prefix}/products`,
      },
      {
        label: "Go to Sales Orders",
        icon: <CreditCard size={20} color="#2ba84a" />,
        bg: "rgba(43,168,74,0.12)",
        generatePath: (prefix: string) => `/${prefix}/orders`,
      },
      {
        label: "Manage Team & Roles",
        icon: <Users size={20} color="#4f39f6" />,
        bg: "#e0e7ff",
      },
    ],
  },

  SIGNAGE_TEAM_MEMBER: null as any, // auto maps later

  // -------------------------------------------------------------------
  // BROKERAGE + TEAM MEMBER
  // -------------------------------------------------------------------
  BROKERAGE_FIRM: {
    hideTips: false,
    heroTitle: "RealEsy — Brokerage Hub",
    heroSubtitle:
      "Your centralized workspace for managing agents, offices, signage jobs, inventory, documents, vendors, and billing — all in one place.",

    whatYouCanDo: [
      {
        icon: <ClipboardList size={24} color="#155dfc" />,
        iconBg: "#dbeafe",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Installations & Removals",
        desc: "Create, approve, and track all signage jobs from your agents. Monitor installer updates, SLA performance, and photo proof in real time.",
        generatePath: (prefix: string) => `/${prefix}/jobs/installations`,
      },
      {
        icon: <FolderOpen size={24} color="#9810fa" />,
        iconBg: "#f3e8ff",
        iconBorder: "1px solid rgba(184,75,255,0.18)",
        title: "WorkDrive (Brokerage File Management)",
        desc: "Organize and access listing documents, compliance files, marketing assets, office-level documents, and agent uploads. Everything stored, shared, and permissioned in one secure workspace.",
        generatePath: (prefix: string) => `/${prefix}/workdrive`,
      },
      {
        icon: <Users size={24} color="#2ba84a" />,
        iconBg: "rgba(43,168,74,0.12)",
        iconBorder: "1px solid rgba(43,168,74,0.18)",
        title: "Agent & Team Management",
        desc: "Add agents, assign roles, control office visibility, and oversee activity across all offices.",
        generatePath: (prefix: string) => `/${prefix}/agents`,
      },
      {
        icon: <Building2 size={24} color="#f54a00" />,
        iconBg: "#ffedd4",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Office Management",
        desc:
          "Set up office locations, managers, and configuration settings that influence job assignment and inventory usage.",
        generatePath: (prefix: string) => `/${prefix}/offices`,
      },
      {
        icon: <Package size={24} color="#4f39f6" />,
        iconBg: "#e0e7ff",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Full Brokerage Inventory",
        desc: "Track all assets your brokerage uses, including sign panels, riders, lockboxes, staging items, frames, flags, brochure boxes, and any custom products you add. RealEsy supports unlimited product types — anything becomes trackable inventory.",
        generatePath: (prefix: string) => `/${prefix}/officeInventory`,
      },
      {
        icon: <MessageSquare size={24} color="#0092b8" />,
        iconBg: "#cefafe",
        iconBorder: "1px solid rgba(184,75,255,0.18)",
        title: "Messaging & Vendor Communication",
        desc: "Use the integrated chat system to communicate with signage vendors and service providers. Share files, ask questions, and maintain a clean history of all interactions.",
      },
      {
        icon: <Handshake size={24} color="#e60076" />,
        iconBg: "#fce7f3",
        iconBorder: "1px solid rgba(43,168,74,0.18)",
        title: "Vendor Network",
        desc: "View vendor performance, pricing, SLA metrics, coverage areas, and assign preferred vendors per office.",
        generatePath: (prefix: string) => `/${prefix}/suppliers`,
      },
      {
        icon: <CreditCard size={24} color="#096" />,
        iconBg: "#d0fae5",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Billing & Payments",
        desc:
          "Track all brokerage-level billing, agent charges, outstanding balances, and detailed spend reports.",
        generatePath: (prefix: string) => `/${prefix}/workdrive`,
      },
    ],

    tips: [
      {
        bg: "#eff6ff",
        bordercolor: "#bedbff",
        icon: <Target size={22} color="#364153" />,
        title: "Increase Agent Adoption",
        text: "The more agents use RealEsy, the smoother your operations become",
        items: [
          "No manual forms",
          "Fewer emails",
          "Complete job visibility",
          "Better vendor coordination",
        ],
      },
      {
        bg: "#faf5ff",
        bordercolor: "#e9d4ff",
        icon: <Package size={22} color="#364153" />,
        title: "Keep Inventory Updated Across Offices",
        text:
          "With all assets tracked in one place, your team avoids shortages, misplacements, and delays.",
      },
      {
        bg: "#f0fdf4",
        bordercolor: "#b9f8cf",
        icon: <TrendingUp size={22} color="#364153" />,
        title: "Choose Preferred Vendors for Better SLAs",
        text:
          "Helps maintain consistency, speed, and higher agent satisfaction.",
      },
      {
        bg: "#fff7ed",
        bordercolor: "#ffd7a8",
        icon: <Shield size={22} color="#364153" />,
        title: "Store Everything in WorkDrive",
        text:
          "Centralized documents reduce compliance issues, lost files, and miscommunication.",
      },
      {
        bg: "#fefce8",
        bordercolor: "#fff085",
        icon: <DollarSign size={22} color="#364153" />,
        title: "Monitor Outstanding Balances",
        text:
          "Staying current prevents vendors from pausing services and keeps job flow uninterrupted.",
      },
      {
        bg: "#fdf2f8",
        bordercolor: "#fccee8",
        icon: <MapPin size={22} color="#364153" />,
        title: "Keep Office & Agent Info Correct",
        text:
          "Minimizes delivery mistakes, missed installs, and routing issues.",
      },
    ],

    quickActions: [
      {
        label: "Create Installations",
        icon: <ClipboardList size={24} color="#155dfc" />,
        bg: "#dbeafe",
        generatePath: (prefix: string) => `/${prefix}/jobs/addInstallation`,
      },
      {
        label: "Create Removals",
        icon: <ClipboardList size={24} color="#155dfc" />,
        bg: "#dbeafe",
        generatePath: (prefix: string) => `/${prefix}/jobs/addRemovalRequest`,
      },

      {
        label: "Manage Agents",
        icon: <Users size={24} color="#2ba84a" />,
        bg: "rgba(43,168,74,0.12)",
        generatePath: (prefix: string) => `/${prefix}/agents`,
      },
      {
        label: "Open WorkDrive",
        icon: <FolderOpen size={24} color="#9810fa" />,
        bg: "#f3e8ff",
        generatePath: (prefix: string) => `/${prefix}/workdrive`,
      },
      {
        label: "Go to Inventory",
        icon: <Package size={24} color="#4f39f6" />,
        bg: "#e0e7ff",
        generatePath: (prefix: string) => `/${prefix}/officeInventory`,
      },
      {
        label: "View vendors",
        icon: <Handshake size={24} color="#e60076" />,
        bg: "#fce7f3",
        generatePath: (prefix: string) => `/${prefix}/suppliers`,
      },
      {
        label: "Billing & Payments",
        icon: <CreditCard size={24} color="#096" />,
        bg: "#d0fae5",
        generatePath: (prefix: string) => `/${prefix}/workdrive`,
      },
      {
        label: "Go to Offices",
        icon: <Building2 size={24} color="#f54a00" />,
        bg: "#ffedd4",
        generatePath: (prefix: string) => `/${prefix}/offices`,
      },
    ],
  },

  BROKERAGE_TEAM_MEMBER: null as any,

  // -------------------------------------------------------------------
  // AGENT
  // -------------------------------------------------------------------
AGENT: {
    hideTips: true, // HIDE TIPS SECTION

    heroTitle: "RealEsy — Agent Hub",
    heroSubtitle:
      "Your streamlined workspace for installations, removals, inventory, documents, and vendor services — all in one place.",

    whatYouCanDo: [
      {
        icon: <ClipboardList size={24} color="#155dfc" />,
        iconBg: "#dbeafe",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Installations Made Simple",
        desc:
          "Request installation in seconds, choose products, add instructions, upload photos, and track progress.",
        path: "/agent/jobs/addInstallation",
      },
      {
        icon: <MapPin size={24} color="#fa7f2d" />,
        iconBg: "rgba(250,127,45,0.12)",
        iconBorder: "1px solid rgba(250,127,45,0.18)",
        title: "Fast Removals",
        desc:
          "Request removals quickly and receive instant updates when completed.",
        path: "/agent/jobs/addRemovalRequest",
      },
      {
        icon: <Package size={24} color="#4f39f6" />,
        iconBg: "#e0e7ff",
        title: "Full Inventory Control",
        text: "Add your own products or use brokerage-provided items. Hand over inventory to vendors through direct allocation, job-based usage, warehouse pickup, or field restock on removals. Always know what's in use, available, or returned.",
        items: [
          "Direct allocation",
          "Job-based usage",
          "Warehouse pickup",
          "Field restock on removals",
        ],
        path: "/agent/inventory",
      },
      {
        icon: <Store size={24} color="#9810fa" />,
        iconBg: "#f3e8ff",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Vendor Marketplace",
        desc:
          "Browse a curated list of signage vendors and other service providers. Compare pricing, turnaround times, profiles, and ratings before selecting a vendor.",
        path: "/agent/suppliers",
      },
      {
        icon: <Tag size={24} color="#00a63e" />,
        iconBg: "#dcfce7",
        iconBorder: "1px solid rgba(250,127,45,0.18)",
        title: "Discount Options",
        desc:
          "Take advantage of available vendor or brokerage discounts for installations, removals, and additional services.",
      },
      {
        icon: <FolderOpen size={24} color="#f54a00" />,
        iconBg: "#ffedd4",
        iconBorder: "1px solid rgba(139,72,255,0.18)",
        title: "WorkDrive (Your Listing Files)",
        desc:
          "Store listing documents, disclosures, marketing materials, photos, and more — accessible from anywhere.",
        path: "/agent/workdrive",
      },
      {
        icon: <Zap size={24} color="#d08700" />,
        iconBg: "#fef9c2",
        title: "Productivity Tools",
        text: "RealEsy helps you work faster with clean listing views, quick job creation, easy inventory management, centralized file storage, vendor chat, and mobile app access for on-the-go updates. All designed to reduce admin work and speed up your listing workflow.",
        items: [
          "Clean listing views",
          "Quick job creation",
          "Easy inventory management",
          "Centralized file storage",
          "Vendor chat",
          "Mobile app access for on-the-go updates",
        ],
      },
    ],

    quickActions: [
      {
        label: "Create Installations",
        icon: <ClipboardList size={24} color="#155dfc" />,
        bg: "#dbeafe",
        path: "/agent/jobs/addInstallation",
      },
      {
        label: "Create Removals",
        icon: <MapPin size={24} color="#e7000b" />,
        bg: "#ffe2e2",
        path: "/agent/jobs/addRemovalRequest",
      },
      {
        label: "Manage My Inventory",
        icon: <Package size={24} color="#4f39f6" />,
        bg: "#e0e7ff",
        path: "/agent/inventory",
      },
      {
        label: "View vendors",
        icon: <Store size={24} color="#9810fa" />,
        bg: "#f3e8ff",
        path: "/agent/suppliers",
      },
      {
        label: "Open WorkDrive",
        icon: <FolderOpen size={24} color="#f54a00" />,
        bg: "#ffedd4",
        path: "/agent/workdrive",
      },
      {
        label: "View My Listings",
        icon: <ClipboardList size={24} color="#00a63e" />,
        bg: "#dcfce7",
        path: "/agent/agents",
      },

    ],
  },

  BROKERAGE_AGENT: {
    hideTips: true, // HIDE TIPS SECTION

    heroTitle: "RealEsy — Agent Hub",
    heroSubtitle:
      "Your streamlined workspace for installations, removals, inventory, documents, and vendor services — all in one place.",

    whatYouCanDo: [
      {
        icon: <ClipboardList size={24} color="#155dfc" />,
        iconBg: "#dbeafe",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Installations Made Simple",
        desc:
          "Request installation in seconds, choose products, add instructions, upload photos, and track progress.",
        path: "/agent/jobs/addInstallation",
      },
      {
        icon: <MapPin size={24} color="#fa7f2d" />,
        iconBg: "rgba(250,127,45,0.12)",
        iconBorder: "1px solid rgba(250,127,45,0.18)",
        title: "Fast Removals",
        desc:
          "Request removals quickly and receive instant updates when completed.",
        path: "/agent/jobs/addRemovalRequest",
      },
      {
        icon: <Package size={24} color="#4f39f6" />,
        iconBg: "#e0e7ff",
        title: "Full Inventory Control",
        text: "Add your own products or use brokerage-provided items. Hand over inventory to vendors through direct allocation, job-based usage, warehouse pickup, or field restock on removals. Always know what's in use, available, or returned.",
        items: [
          "Direct allocation",
          "Job-based usage",
          "Warehouse pickup",
          "Field restock on removals",
        ],
        path: "/agent/inventory",
      },
      {
        icon: <Store size={24} color="#9810fa" />,
        iconBg: "#f3e8ff",
        iconBorder: "1px solid rgba(21,93,252,0.18)",
        title: "Vendor Marketplace",
        desc:
          "Browse a curated list of signage vendors and other service providers. Compare pricing, turnaround times, profiles, and ratings before selecting a vendor.",
        path: "/agent/suppliers",
      },
      {
        icon: <Tag size={24} color="#00a63e" />,
        iconBg: "#dcfce7",
        iconBorder: "1px solid rgba(250,127,45,0.18)",
        title: "Discount Options",
        desc:
          "Take advantage of available vendor or brokerage discounts for installations, removals, and additional services.",
      },
      {
        icon: <FolderOpen size={24} color="#f54a00" />,
        iconBg: "#ffedd4",
        iconBorder: "1px solid rgba(139,72,255,0.18)",
        title: "WorkDrive (Your Listing Files)",
        desc:
          "Store listing documents, disclosures, marketing materials, photos, and more — accessible from anywhere.",
        path: "/agent/workdrive",
      },
      {
        icon: <Zap size={24} color="#d08700" />,
        iconBg: "#fef9c2",
        title: "Productivity Tools",
        text: "RealEsy helps you work faster with clean listing views, quick job creation, easy inventory management, centralized file storage, vendor chat, and mobile app access for on-the-go updates. All designed to reduce admin work and speed up your listing workflow.",
        items: [
          "Clean listing views",
          "Quick job creation",
          "Easy inventory management",
          "Centralized file storage",
          "Vendor chat",
          "Mobile app access for on-the-go updates",
        ],
      },
    ],

    quickActions: [
      {
        label: "Create Installations",
        icon: <ClipboardList size={24} color="#155dfc" />,
        bg: "#dbeafe",
        path: "/agent/jobs/addInstallation",
      },
      {
        label: "Create Removals",
        icon: <MapPin size={24} color="#e7000b" />,
        bg: "#ffe2e2",
        path: "/agent/jobs/addRemovalRequest",
      },
      {
        label: "Manage My Inventory",
        icon: <Package size={24} color="#4f39f6" />,
        bg: "#e0e7ff",
        path: "/agent/inventory",
      },
      {
        label: "View vendors",
        icon: <Store size={24} color="#9810fa" />,
        bg: "#f3e8ff",
        path: "/agent/suppliers",
      },
      {
        label: "Open WorkDrive",
        icon: <FolderOpen size={24} color="#f54a00" />,
        bg: "#ffedd4",
        path: "/agent/workdrive",
      },
      {
        label: "View My Listings",
        icon: <ClipboardList size={24} color="#00a63e" />,
        bg: "#dcfce7",
        path: "/agent/agents",
      },

    ],
  },
};

// team members → same config as parent
welcomeConfig.SIGNAGE_TEAM_MEMBER = welcomeConfig.SIGNAGE_FIRM;
welcomeConfig.BROKERAGE_TEAM_MEMBER = welcomeConfig.BROKERAGE_FIRM;

